import { MoreHoriz, SwapHoriz } from '@mui/icons-material';

import { IconButton, MenuItem, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SubscriptionService, useApiEffect, useRoutes, UserService } from '@localstack/services';
import {
  AcquisitionChannel,
  BillingChannel,
  CancellationDetails,
  EmulatorType,
  CreditCard,
  Permission,
  PlanFamily,
  Subscription,
  SubscriptionStatus,
} from '@localstack/types';

import { Dropdown, DropdownProps, SubscriptionStatusWithCancelling, whatIsTheStatus } from '@localstack/ui';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { CANCELABLE_PLAN_FAMILIES } from '~/constants';

import { pricingRoute } from '~/util/pricing';

import { SubscriptionUpdateMenuItem } from './SubscriptionUpdateMenuItem';
import { UsageMetric } from './metricColumns';

import { SubscriptionCancellationMenuItem } from './SubscriptionCancellationMenuItem';

import { SubscriptionDetails, SubscriptionDetailsType } from './SubscriptionDetails';
import { SubscriptionOverviewColumn } from './SubscriptionOverviewColumn';

const StyledDropdown = styled(Dropdown)(({ theme }) => ({
  minWidth: theme.spacing(25),
  marginLeft: theme.spacing(5),
}));

type TooltipConfig = {
  /** Whether the actions button should be disabled */
  disabled: boolean;
  /** The tooltip text */
  message: string;
  /** Whether the tooltip should appear regardless of other conditions such as if plan is updatable or user permissions */
  shouldAlwaysShow?: boolean;
};

const getTooltipConfig = (subscription: Subscription, status: SubscriptionStatusWithCancelling): TooltipConfig => {
  if (
    subscription.acquisition_channel === AcquisitionChannel.SALES ||
    subscription.plan.family === PlanFamily.ENTERPRISE_PLANS
  ) {
    return {
      disabled: true,
      message: 'Please contact sales to manage your subscription',
      shouldAlwaysShow: true,
    };
  }
  if (status === 'cancelling') {
    return {
      disabled: true,
      message: 'Cannot update a subscription that is being cancelled',
    };
  }
  if (subscription.billing_channel === BillingChannel.AWS_MARKETPLACE) {
    return {
      disabled: true,
      message: 'Subscription is managed through AWS Marketplace',
    };
  }

  return {
    disabled: false,
    message: '',
  };
};

const DisabledSubActionsWithTooltip = ({ title }: { title: string }): JSX.Element => (
  <Tooltip title={title}>
    <span>
      <IconButton disabled aria-label="subscription options">
        <MoreHoriz />
      </IconButton>
    </span>
  </Tooltip>
);

const SubActions = ({
  isSwitchable,
  isUpdatable,
  isCancellable,
  isLoading,
  subscription,
  subscriptionsDetails,
  onCancelSubscription,
}: {
  /** Whether the subscription can be switched to a different plan */
  isSwitchable: boolean;
  /** Whether the subscription can have its seats updated */
  isUpdatable: boolean;
  isCancellable: boolean;
  isLoading: boolean;
  subscription: Subscription;
  subscriptionsDetails: SubscriptionDetailsType;
  onCancelSubscription: (id: string, details?: CancellationDetails) => void;
}): JSX.Element => {
  const { goto } = useRoutes();

  return (
    <StyledDropdown
      size="small"
      menuListProps={{
        disablePadding: true,
      }}
      renderButton={({ onClick }: DropdownProps) => (
        <IconButton onClick={onClick} aria-label="subscription options">
          <MoreHoriz />
        </IconButton>
      )}
    >
      {isSwitchable && (
        <MenuItem onClick={() => goto(pricingRoute([subscription.plan.emulator], subscription.id))}>
          <SwapHoriz fontSize="small" />
          <Typography variant="inherit" sx={{ marginLeft: '0.5em' }}>
            Change plan
          </Typography>
        </MenuItem>
      )}
      {isUpdatable && (
        <SubscriptionUpdateMenuItem subscriptionDetails={subscriptionsDetails} subscription={subscription} />
      )}
      {isCancellable && (
        <SubscriptionCancellationMenuItem
          isLoading={isLoading}
          subscription={subscription}
          onCancelSubscription={onCancelSubscription}
        />
      )}
    </StyledDropdown>
  );
};

/** Contains the information that should be shown for a single subscription in the Subscriptions section of the Billing page */
export const SubscriptionRow = ({
  subscription,
  subscriptionsDetails,
  cards,
  emulators,
  metricColumns,
}: {
  subscription: Subscription;
  subscriptionsDetails: SubscriptionDetailsType;
  metricColumns: UsageMetric[];
  cards: Optional<CreditCard[]>;
  /** The emulators this subscription gives access to. */
  emulators: EmulatorType[];
}): JSX.Element => {
  const { can } = useAuthProvider();

  const hasCompleteSubscription =
    subscription?.status !== SubscriptionStatus.INCOMPLETE && subscription?.status !== SubscriptionStatus.UNPAID;

  const isCancellable =
    subscription?.plan?.family &&
    can(Permission.DELETE_SUBSCRIPTIONS) &&
    CANCELABLE_PLAN_FAMILIES.includes(subscription.plan.family) &&
    hasCompleteSubscription &&
    subscription.billing_channel !== BillingChannel.AWS_MARKETPLACE;

  const isTrialOrPreviewPlan =
    subscription?.plan?.family === PlanFamily.TRIAL_PLANS || subscription?.plan?.family === PlanFamily.PREVIEW_PLANS;
  const canPayForUpdate = !!cards && cards.length > 0;

  const canUpdate = can(Permission.UPDATE_SUBSCRIPTIONS) && hasCompleteSubscription;
  const isSwitchable = canUpdate && !!subscription?.plan?.switchable;
  const isUpdatable = canUpdate && !!subscription?.plan?.updatable;

  const status = whatIsTheStatus(subscription);
  const tooltipConfig = getTooltipConfig(subscription, status);

  const { cancelSubscription, isLoading: isSubOrKeyMutating } = useApiEffect(
    SubscriptionService,
    ['cancelSubscription'],
    {
      revalidate: ['listCiTokens', 'listSubscriptions', 'getSubscription', 'getAggregatedSubscriptionInfo'],
      revalidateOtherClient: {
        client: [UserService],
        methods: ['getUser'],
      },
    },
  );

  const showTooltipOrActions = tooltipConfig.shouldAlwaysShow || isCancellable || isUpdatable || isSwitchable;

  return (
    <>
      <SubscriptionOverviewColumn subscription={subscription} status={status} emulators={emulators} />
      <SubscriptionDetails
        subscription={subscription}
        subscriptionDetails={subscriptionsDetails}
        columns={metricColumns}
      />
      {!showTooltipOrActions && <span />}
      {showTooltipOrActions &&
        (tooltipConfig.disabled ? (
          <DisabledSubActionsWithTooltip title={tooltipConfig.message} />
        ) : (
          <SubActions
            isSwitchable={isSwitchable}
            isCancellable={isCancellable}
            isUpdatable={isUpdatable && (canPayForUpdate || isTrialOrPreviewPlan)}
            isLoading={isSubOrKeyMutating}
            subscription={subscription}
            subscriptionsDetails={subscriptionsDetails}
            onCancelSubscription={async (id, details) => {
              await cancelSubscription(id, details);
            }}
          />
        ))}
    </>
  );
};
